import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Button, CopyText, Heading, Icon, Loader, Select } from "@stellar/design-system";

import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { Pagination } from "@/components/Pagination";
import { SearchInput } from "@/components/SearchInput";
import { SectionHeader } from "@/components/SectionHeader";

import { PAGE_LIMIT_OPTIONS } from "@/constants/settings";

import { getDataExplorerRecovery, RecoveryReportItem } from "@/api/getDataExplorerRecovery";
import { getDataExplorerUsers, DataExplorerUser } from "@/api/getDataExplorerUsers";

type Tab = "users" | "recovery";

export const DataExplorer = () => {
  const [activeTab, setActiveTab] = useState<Tab>("users");

  return (
    <>
      {activeTab === "users" && <UsersTable activeTab={activeTab} onTabChange={setActiveTab} />}
      {activeTab === "recovery" && <RecoveryTable activeTab={activeTab} onTabChange={setActiveTab} />}
    </>
  );
};

interface TabHeaderProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
}

const UsersTable = ({ activeTab, onTabChange }: TabHeaderProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [searchQuery, setSearchQuery] = useState<{ q: string } | undefined>();

  const {
    data: response,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "dataExplorerUsers",
      currentPage,
      pageLimit,
      searchQuery?.q ?? "",
    ],
    queryFn: () =>
      getDataExplorerUsers({
        page: currentPage,
        page_limit: pageLimit,
        q: searchQuery?.q || undefined,
      }),
  });

  const users = response?.users ?? [];
  const totalPages = Math.max(response?.pagination.pages ?? 1, 1);
  const isSearchInProgress = Boolean(searchQuery && (isLoading || isFetching));

  const handleSearchChange = (searchText?: string) => {
    setCurrentPage(1);
    setSearchQuery(searchText ? { q: searchText } : undefined);
  };

  const handlePageLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    setPageLimit(Number(event.target.value));
  };

  return (
    <>
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm">
              Data Explorer
            </Heading>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <Button
              variant={activeTab === "users" ? "secondary" : "tertiary"}
              size="sm"
              icon={<Icon.Users01 />}
              onClick={() => onTabChange("users")}
            >
              User Mapping
            </Button>
            <Button
              variant={activeTab === "recovery" ? "secondary" : "tertiary"}
              size="sm"
              icon={<Icon.ShieldTick />}
              onClick={() => onTabChange("recovery")}
            >
              Assisted Recovery
            </Button>
          </SectionHeader.Content>
        </SectionHeader.Row>

        <SectionHeader.Row>
          <SectionHeader.Content>
            <div className="FiltersWithSearch__search">
              <SearchInput
                id="data-explorer-search"
                placeholder="Search by email, phone, public key, or ID"
                onSubmit={handleSearchChange}
                onClear={handleSearchChange}
                isLoading={isSearchInProgress}
              />
            </div>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <div className="FiltersWithSearch__pageLimit">
              <Select
                id="users-page-limit"
                fieldSize="md"
                value={pageLimit}
                onChange={handlePageLimitChange}
              >
                {PAGE_LIMIT_OPTIONS.map((p) => (
                  <option key={p} value={p}>{`Show ${p} results`}</option>
                ))}
              </Select>
            </div>

            <Pagination
              currentPage={currentPage}
              maxPages={totalPages}
              onSetPage={setCurrentPage}
              isLoading={isLoading || isFetching}
            />
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      {error && <ErrorWithExtras appError={{ message: String(error) }} />}

      {response?.warnings?.length ? (
        <div className="Note Note--warning">
          {response.warnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
        </div>
      ) : null}

      <div className="TableContainer">
        <table className="Table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Stellar Address</th>
              <th>External ID</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>
                  <Loader />
                </td>
              </tr>
            )}

            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>
                  No users found
                </td>
              </tr>
            )}

            {!isLoading &&
              users.map((user: DataExplorerUser) => (
                <tr key={`${user.tenant_name}-${user.receiver_id}`}>
                  <td>{user.tenant_name}</td>
                  <td>{user.email || "—"}</td>
                  <td>{user.phone_number || "—"}</td>
                  <td className="Table__cell--mono">
                    {user.stellar_address ? (
                      <CopyText textToCopy={user.stellar_address}>
                        <span style={{ wordBreak: "break-all", whiteSpace: "normal" }}>
                          {user.stellar_address}
                        </span>
                      </CopyText>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{user.external_id || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const RecoveryTable = ({ activeTab, onTabChange }: TabHeaderProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: response,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["dataExplorerRecovery", currentPage, pageLimit, searchQuery],
    queryFn: () =>
      getDataExplorerRecovery({
        page: currentPage,
        limit: pageLimit,
        q: searchQuery || undefined,
      }),
    placeholderData: (previousData) => previousData,
  });

  const items = response?.data ?? [];
  const totalPages = Math.max(response?.pagination?.totalPages ?? 1, 1);
  const isSearchInProgress = Boolean(searchQuery && (isLoading || isFetching));

  const handleSearchChange = (searchText?: string) => {
    setCurrentPage(1);
    setSearchQuery(searchText?.trim() ?? "");
  };

  const handlePageLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    setPageLimit(Number(event.target.value));
  };

  return (
    <>
      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm">
              Data Explorer
            </Heading>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <Button
              variant={activeTab === "users" ? "secondary" : "tertiary"}
              size="sm"
              icon={<Icon.Users01 />}
              onClick={() => onTabChange("users")}
            >
              User Mapping
            </Button>
            <Button
              variant={activeTab === "recovery" ? "secondary" : "tertiary"}
              size="sm"
              icon={<Icon.ShieldTick />}
              onClick={() => onTabChange("recovery")}
            >
              Assisted Recovery
            </Button>
          </SectionHeader.Content>
        </SectionHeader.Row>

        <SectionHeader.Row>
          <SectionHeader.Content>
            <div className="FiltersWithSearch__search">
              <SearchInput
                id="data-explorer-recovery-search"
                placeholder="Search by email, phone, or Stellar address"
                onSubmit={handleSearchChange}
                onClear={handleSearchChange}
                isLoading={isSearchInProgress}
              />
            </div>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <div className="FiltersWithSearch__pageLimit">
              <Select
                id="recovery-page-limit"
                fieldSize="md"
                value={pageLimit}
                onChange={handlePageLimitChange}
              >
                {PAGE_LIMIT_OPTIONS.map((p) => (
                  <option key={p} value={p}>{`Show ${p} results`}</option>
                ))}
              </Select>
            </div>

            <Pagination
              currentPage={currentPage}
              maxPages={totalPages}
              onSetPage={setCurrentPage}
              isLoading={isLoading || isFetching}
            />
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      {error && <ErrorWithExtras appError={{ message: String(error) }} />}

      <div className="TableContainer">
        <table className="Table" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Stellar Account</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Last Event</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "2rem" }}>
                  <Loader />
                </td>
              </tr>
            )}

            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "2rem" }}>
                  No recovery records found
                </td>
              </tr>
            )}

            {!isLoading &&
              items.map((item: RecoveryReportItem) => (
                <tr key={item.transactionId}>
                  <td>{item.organizationName || "—"}</td>
                  <td className="Table__cell--mono">
                    {item.userStellarAccount ? (
                      <CopyText textToCopy={item.userStellarAccount}>
                        <span style={{ wordBreak: "break-all", whiteSpace: "normal" }}>
                          {item.userStellarAccount}
                        </span>
                      </CopyText>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{item.email || "—"}</td>
                  <td>{item.phoneNumber || "—"}</td>
                  <td>{item.latestStatus}</td>
                  <td>{item.lastEventType}</td>
                  <td>{item.lastEventAt}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
